import 'swiper/css';
import 'swiper/css/pagination';
import Swiper from "swiper";
import {Navigation, Pagination, Autoplay} from "swiper/modules"

const Slider = {};

Slider.default = {};

const autoHidePaginations = true;

Slider.default.classes = {
    slider: 'slider-elt',
    container: null,
    next: 'slider-next-elt',
    prev: 'slider-prev-elt',
    page: 'slider-current-page',
    pagination: 'slider-pagination-elt',
    desktopNavigation: 'slider-desktop-navigation',
    mobileNavigation: 'slider-mobile-navigation',
}

Slider.default.config = {
    modules: [Navigation, Pagination, Autoplay],
    direction: 'horizontal',
    loop: true,
    spaceBetween: 24,
    slidesPerView: 1,
}

const getEltInContainer = (container, className, multiple = false) => {
    if (multiple) {
        return container.querySelectorAll(`.${className}`);
    }

    return container.querySelector(`.${className}`);
};

const getConfig = (data) => {
    const config = {...Slider.default.config, ...data.config};

    if (data.next || data.prev) {
        config.navigation = {
            nextEl: data.next[0],
            prevEl: data.prev[0],
            additionalNextEls: [],
            additionalPrevEls: [],
        };

        if (data.next) {
            for (let i = 1; i < data.next.length; i++) {
                config.navigation.additionalNextEls.push(data.next[i]);
            }
        }

        if (data.prev) {
            for (let i = 1; i < data.prev.length; i++) {
                config.navigation.additionalPrevEls.push(data.prev[i]);
            }
        }
    }

    if (data.pagination) {
        config.pagination = {
            el: data.pagination,
            type: data.paginationType ?? 'progressbar',
            clickable: false,
        };
    }

    config.breakpoints = data.breakpoints;

    return config;
};

const updateSliderPage = (sliderData, value) => {
    if (sliderData.page) {
        sliderData.page.forEach(page => {
            page.innerHTML = value;
        });
    }
};

const getSliders = (classes, config, paginationType) => {
    if (!window.sliders) {
        window.sliders = {};
    }

    if (!window.sliders[classes.slider]) {
        window.sliders[classes.slider] = [];

        const containers = [...document.getElementsByClassName(classes.container)];

        containers.forEach(container => {
            let sliders;

            if (classes.container === classes.slider) {
                const data = {
                    slider: container,
                    container: container,
                    next: getEltInContainer(container, classes.next, true),
                    prev: getEltInContainer(container, classes.prev, true),
                    page: getEltInContainer(container, classes.page, true),
                    pagination: getEltInContainer(container, classes.pagination),
                    desktopNavigation: getEltInContainer(container, classes.desktopNavigation),
                    mobileNavigation: getEltInContainer(container, classes.mobileNavigation),
                    paginationType: paginationType,
                    slidesCount: getEltInContainer(elt, 'swiper-slide', true).length,
                };

                data.config = {...getConfig(data), ...config};

                window.sliders[classes.slider].push(data);
            } else {
                sliders = [...container.getElementsByClassName(classes.slider)];

                sliders.forEach(elt => {
                    const data = {
                        slider: elt,
                        container: container,
                        next: getEltInContainer(container, classes.next, true),
                        prev: getEltInContainer(container, classes.prev, true),
                        page: getEltInContainer(container, classes.page, true),
                        pagination: getEltInContainer(container, classes.pagination),
                        desktopNavigation: getEltInContainer(container, classes.desktopNavigation),
                        mobileNavigation: getEltInContainer(container, classes.mobileNavigation),
                        paginationType: paginationType,
                        slidesCount: getEltInContainer(elt, 'swiper-slide', true).length,
                    };

                    data.config = {...getConfig(data), ...config};

                    window.sliders[classes.slider].push(data);
                });
            }
        });
    }

    return window.sliders;
};

const disablePrevButtons = (sliderData) => {
    sliderData.prev.forEach(prev => {
        prev.setAttribute('disabled', 'disabled');
    });
};

const enablePrevButtons = (sliderData) => {
    sliderData.prev.forEach(prev => {
        prev.removeAttribute('disabled');
    });
};

const disableNextButtons = (sliderData) => {
    sliderData.next.forEach(next => {
        next.setAttribute('disabled', 'disabled');
    });
};

const enableNextButtons = (sliderData) => {
    sliderData.next.forEach(next => {
        next.removeAttribute('disabled');
    });
};

/**
 * Returns the current number of slides per view
 * @param sliderData
 */
const getCurrentSlidesPerView = (sliderData) => {
    const config = getCurrentBreakpointConfig(sliderData);

    if (config?.slidesPerView) {
        return config.slidesPerView;
    }

    return 1;
};

/**
 * Returns an array of breakpoints (int values)
 * @param sliderData
 * @returns {number[]}
 */
const getBreakpoints = (sliderData) => {
    const breakpoints = [0];

    if (sliderData?.config?.breakpoints) {
        Object.keys(sliderData.config.breakpoints).forEach(key => {
            breakpoints.push(parseInt(key));
        });
    }

    return breakpoints;
};

/**
 * Returns the current slider breakpoint
 * @param sliderData
 * @returns {number}
 */
const getCurrentBreakpoint = (sliderData) => {
    const breakpoints = getBreakpoints(sliderData);
    const width = document.documentElement.clientWidth;

    if (breakpoints.length === 1) {
        return breakpoints[0];
    }

    let breakpoint = 0;

    breakpoints.forEach(value => {
        if (value < width) {
            breakpoint = value;
        }
    });

    return breakpoint;
};

/**
 * Returns the slider config (only for the current viewport)
 * @param sliderData
 * @returns {*}
 */
const getCurrentBreakpointConfig = (sliderData) => {
    const breakpoint = getCurrentBreakpoint(sliderData);
    let config = JSON.parse(JSON.stringify(sliderData.config));

    const breakpointsSpecificConfigs = config.breakpoints;

    delete config.breakpoints;

    if (breakpointsSpecificConfigs && breakpointsSpecificConfigs[breakpoint]) {
        config = {...config, ...breakpointsSpecificConfigs[breakpoint]};
    }

    return config;
};

/**
 * Manage activated / deactivated states of prev / next buttons
 * @param sliderData
 */
const handleButtons = (sliderData) => {
    if (!sliderData?.config?.loop) {
        const getCurrentSlide = getSliderCurrentIndex(sliderData.instance);
        const slidesPerView = getCurrentSlidesPerView(sliderData);

        if (sliderData.slidesCount > 1) {
            switch (getCurrentSlide + (slidesPerView - 1)) {
                case slidesPerView:
                    disablePrevButtons(sliderData);
                    enableNextButtons(sliderData);
                    break;
                case sliderData.slidesCount:
                    disableNextButtons(sliderData);
                    enablePrevButtons(sliderData);
                    break;
                default:
                    enableNextButtons(sliderData);
                    enablePrevButtons(sliderData);
                    break;
            }
        }
    }
};

/**
 * Manage visible / hidden states of paginations
 * @param sliderData
 */
const handlePaginations = (sliderData) => {
    if (getCurrentBreakpoint(sliderData) !== sliderData.currentBreakpoint) {
        sliderData.currentBreakpoint = getCurrentBreakpoint(sliderData);

        if (autoHidePaginations) {
            if (sliderData?.slidesCount < getCurrentSlidesPerView(sliderData)) {
                if (sliderData?.desktopNavigation) {
                    sliderData.desktopNavigation.style.display = 'none';
                }

                if (sliderData.mobileNavigation) {
                    sliderData.mobileNavigation.style.display = 'none';
                }
            } else {
                if (sliderData?.desktopNavigation) {
                    sliderData.desktopNavigation.style.display = '';
                }

                if (sliderData.mobileNavigation) {
                    sliderData.mobileNavigation.style.display = '';
                }
            }
        }
    }
};

const getSliderCurrentIndex = (slider) => {
    return slider.realIndex + 1;
};

/**
 * Init a slider from a slider class
 * @param classes
 * @param config
 * @param pageWithTrailingZero
 */
Slider.init = (classes = {}, config = {}, paginationType = null, pageWithTrailingZero = true) => {
    classes = {...Slider.default.classes, ...classes};

    if (!classes.container) {
        classes.container = classes.slider;
    }

    const slidersToInit = getSliders(classes, config, paginationType);

    if (slidersToInit[classes.slider]) {
        const sliders = slidersToInit[classes.slider];

        sliders.forEach((sliderData, i) => {
            if (sliderData.slider) {
                sliderData.instance = new Swiper(sliderData.slider, sliderData.config);
                sliderData.currentBreakpoint = null;

                sliderData.config.navigation.additionalNextEls.forEach(nextBtn => {
                    nextBtn.addEventListener('click', () => {
                        sliderData.instance.slideNext();
                    });
                });

                sliderData.config.navigation.additionalPrevEls.forEach(prevBtn => {
                    prevBtn.addEventListener('click', () => {
                        sliderData.instance.slidePrev();
                    });
                });

                handlePaginations(window.sliders[classes.slider][i]);

                window.addEventListener('resize', () => {
                    handlePaginations(window.sliders[classes.slider][i]);
                });

                handleButtons(sliderData);

                sliderData.instance.on('slideChange', () => {
                    let numberString;
                    const indexNumber = getSliderCurrentIndex(sliderData.instance);

                    if (pageWithTrailingZero) {
                        numberString = indexNumber < 10 ? `0${indexNumber}` : indexNumber;
                    } else {
                        numberString = indexNumber;
                    }

                    updateSliderPage(sliderData, numberString);
                    handleButtons(sliderData);
                });

                window.sliders[classes.slider][i] = sliderData;
            }
        });
    }
};

export default Slider;
